import React, { useState, useEffect } from 'react';
import {
    Paper,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import googleOneTapService from '../../services/googleOneTapService';
import './LoginDialog.css';

function LoginDialog({ onLogin, error }) {
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            googleOneTapService.initialize();
            googleOneTapService.prompt();
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    const handleGoogleLogin = () => {
        setLoading(true);
        onLogin();
    };

    // Spinning circle with animated comet
    const SpinCircle = ({ size, right, left, bottom, top, duration, direction, cometFrom }) => (
        <Box sx={{
            position: 'absolute',
            ...(right  !== undefined && { right  }),
            ...(left   !== undefined && { left   }),
            ...(top    !== undefined && { top,    transform: 'translateY(-50%)' }),
            ...(bottom !== undefined && { bottom }),
            width: size, height: size,
            borderRadius: '50%',
            border: '1px solid rgba(154,114,9,0.18)',
            pointerEvents: 'none',
        }}>
            <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: `conic-gradient(from ${cometFrom}deg, transparent 93%, rgba(154,114,9,0.1) 95%, rgba(218,165,32,0.5) 96.5%, rgba(255,230,80,0.9) 97.5%, rgba(218,165,32,0.5) 98.5%, rgba(154,114,9,0.08) 99.5%, transparent 100%)`,
                WebkitMask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
                mask:        'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
                animation: `${direction} ${duration} linear infinite`,
            }} />
        </Box>
    );

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: '#1e3a5f',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            direction: 'rtl',
            fontFamily: "'Heebo', sans-serif",
            p: 2,
        }}>

            {/* Static dot grid */}
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(218,165,32,0.06) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
            }} />

            {/* 5 animated gold sparkle layers */}
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(218,165,32,0.22) 1.5px, transparent 1.5px)',
                backgroundSize: '112px 112px',
                animation: 'heroDotTwinkle 2.9s ease-in-out infinite',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(200,155,20,0.16) 1.5px, transparent 1.5px)',
                backgroundSize: '84px 84px', backgroundPosition: '14px 28px',
                animation: 'heroDotTwinkle 3.7s ease-in-out 1.3s infinite',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(255,210,60,0.25) 1.5px, transparent 1.5px)',
                backgroundSize: '140px 112px', backgroundPosition: '42px 14px',
                animation: 'heroDotTwinkle 4.4s ease-in-out 2.5s infinite',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(218,165,32,0.18) 1px, transparent 1px)',
                backgroundSize: '196px 140px', backgroundPosition: '70px 56px',
                animation: 'heroDotTwinkle 5.1s ease-in-out 0.7s infinite',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(255,200,30,0.14) 1px, transparent 1px)',
                backgroundSize: '168px 196px', backgroundPosition: '28px 84px',
                animation: 'heroDotTwinkle 3.3s ease-in-out 2.1s infinite',
            }} />

            {/* Top-right radial glow */}
            <Box sx={{
                position: 'absolute', top: 0, right: 0,
                width: '55vw', height: '70vh', pointerEvents: 'none',
                background: 'radial-gradient(ellipse at top right, rgba(154,114,9,0.2) 0%, transparent 60%)',
            }} />

            {/* Spinning circles */}
            <SpinCircle size="74vw" right="-14%" top="50%" duration="22s" direction="spinCW"  cometFrom={0}   />
            <SpinCircle size="50vw" right="-7%"  top="50%" duration="34s" direction="spinCCW" cometFrom={180} />
            <SpinCircle size="35vw" left="-8%"   bottom="-12%" duration="28s" direction="spinCW" cometFrom={270} />

            {/* Card */}
            <Paper
                elevation={0}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    borderRadius: '20px',
                    p: '44px 40px',
                    backgroundColor: 'rgba(255, 255, 255, 0.97)',
                    border: '1px solid rgba(184, 134, 11, 0.3)',
                    boxShadow: '0 32px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(154,114,9,0.1)',
                    maxWidth: 400,
                    width: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {/* Gold top bar */}
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
                    background: 'linear-gradient(90deg, #9a7209 0%, #f5d060 50%, #9a7209 100%)',
                }} />

                {/* Logo */}
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <img
                        className="login-logo-fadein"
                        src="/inorai-logo.png"
                        alt="inorai"
                        style={{ width: 150, height: 'auto', objectFit: 'contain' }}
                    />
                </Box>

                {/* Headline with gold heartbeat word */}
                <Typography sx={{
                    fontSize: '1.75rem',
                    fontWeight: 800,
                    color: '#1e3a5f',
                    textAlign: 'center',
                    mb: 1,
                    fontFamily: "'Heebo', sans-serif",
                    lineHeight: 1.3,
                }}>
                    <span className="login-gold-pulse">שמחים</span> שהגעתם!
                </Typography>

                {/* Warm subtitle */}
                <Typography sx={{
                    color: '#5a6978',
                    textAlign: 'center',
                    mb: 3.5,
                    fontFamily: "'Heebo', sans-serif",
                    fontSize: '0.95rem',
                    lineHeight: 1.75,
                }}>
                    נעשה את המקסימום כדי שתהיו מרוצים.
                    <br />
                    כל צורך שיש לכם — אנחנו כאן.
                </Typography>

                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                        {error}
                    </Alert>
                )}

                {/* Google Sign-in Button */}
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
                    sx={{
                        mb: 2.5,
                        background: 'linear-gradient(135deg, #1e3a5f 0%, #2c4a6e 100%)',
                        color: '#ffffff',
                        py: 1.75,
                        fontSize: '1rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: '12px',
                        fontFamily: "'Heebo', sans-serif",
                        border: '1px solid rgba(184, 134, 11, 0.4)',
                        boxShadow: '0 4px 20px rgba(30, 58, 95, 0.4)',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #152a47 0%, #1e3a5f 100%)',
                            boxShadow: '0 8px 30px rgba(30, 58, 95, 0.55)',
                            transform: 'translateY(-2px)',
                            borderColor: 'rgba(184, 134, 11, 0.7)',
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        },
                        '&:disabled': {
                            background: 'rgba(30, 58, 95, 0.35)',
                            color: 'rgba(255,255,255,0.5)',
                            border: '1px solid rgba(30, 58, 95, 0.15)',
                        },
                    }}
                >
                    {loading ? 'מתחבר...' : 'התחברות עם Google'}
                </Button>

                {/* Brand tagline */}
                <Typography variant="caption" sx={{
                    color: 'rgba(30, 58, 95, 0.4)',
                    textAlign: 'center',
                    fontFamily: "'Heebo', sans-serif",
                    fontSize: '0.78rem',
                    letterSpacing: '0.02em',
                }}>
                    שילוט דיגיטלי חכם לקהילה היהודית
                </Typography>
            </Paper>
        </Box>
    );
}

export default LoginDialog;
